/**
 * /app/$tenantSlug/overview — Manage → Overview (operator cockpit).
 *
 * docs/58 Slice 2 split the old shared `/` hub into a personal Me home
 * (the index route) and this operator home. Manager+/HR reach it as the
 * Manage side's landing. It carries the widgets that are about running
 * the COMPANY, not about the individual:
 *
 *  - AccountingHero (G_ACC8) — accounting snapshot / empty-state; self-
 *    hides unless the caller is an accounting-admin on an accounting tenant.
 *  - Getting-started setup checklist (HR-admin) — workspace / leave policy
 *    / invite team / POSH committee / choose a plan, each with a REAL
 *    done-signal where one exists (directory roster count, ICC members,
 *    active subscription). Survives the trial→paid conversion; dismissible.
 *  - Module quick-jump grid — the viewer's own Manage items, the same
 *    list as the sidebar (lib/overview-modules.ts).
 *
 * The personal widgets (my queue, day-one, engagement, banners) stay on
 * the Me home. This page is feature/role self-gating throughout, so a
 * plain employee who deep-links here just sees their own jump grid.
 */

import { useMemo } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Inbox,
  ShieldCheck,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth, useFeatures, usePoshOnly } from "@/lib/auth";
import { useNavFlags } from "@/hooks/use-nav-flags";
import { overviewModules } from "@/lib/overview-modules";
import { resolveTenantPath } from "@/components/shared/module-nav";
import { directoryKeys } from "@/hooks/use-directory";
import { useApprovalsSummary } from "@/hooks/use-approvals-summary";
import { useCompliance } from "@/hooks/use-posh";
import { complianceApi, directoryApi, settingsApi } from "@/lib/api";
import { MODULE_META } from "@/lib/module-meta";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AccountingHero } from "@/components/dashboard/accounting-hero";
import { SetupChecklist } from "@/components/dashboard/setup-checklist";
import { FirstRunBanners } from "@/components/dashboard/first-run-banners";
import { NotInPoshOnlyTiles } from "@/components/billing/not-in-posh-only";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/overview")({
  // A9 hover-prefetch: warm the one query this page fires for EVERY role —
  // the active-roster directory (useDirectory() below). The role-gated
  // reads (settings/modules, ICC members) stay component-owned: the loader
  // has no auth context and must never fire a guaranteed-403 request.
  // .catch → the component's own useQuery error path stays the authority.
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData({
        queryKey: directoryKeys.list(false),
        queryFn: ({ signal }) =>
          directoryApi.listDirectory({ includeInactive: false }, signal),
      })
      .catch(() => undefined),
  component: RunOverview,
});

const HR_ADMINS = ["company_admin", "hr_manager"];

function RunOverview() {
  const { user } = useAuth();
  const name = user?.first_name?.trim() || "there";
  const role = user?.role ?? "";
  const features = user?.features ?? [];
  const isHRAdmin = HR_ADMINS.includes(role);
  const hasPosh = features.includes("posh");
  // POSH only (migration 162): no compliance centre; the plan's left-out
  // modules show as "Not in POSH only" tiles instead.
  const platform = features.includes("hr_platform");
  const poshOnly = usePoshOnly();

  // ── "Needs your attention" (2026-08 UX audit) ─────────────────────────
  // Every feed is fail-soft: while loading or on error the row simply
  // doesn't render — this strip must never block or error the page.
  // (a) Pending approvals — role-scoped counts, safe for every caller
  //     (a non-approver gets zeros); same summary the Inbox badge uses.
  const approvalsQ = useApprovalsSummary();
  const pendingApprovals = approvalsQ.data?.pending ?? 0;
  // (b) POSH training non-compliance — HR-admin + posh-feature gated so
  //     the query never fires a guaranteed 403.
  const poshComplianceQ = useCompliance(isHRAdmin && hasPosh);
  const poshNonCompliant = poshComplianceQ.data
    ? poshComplianceQ.data.summary.not_started +
      poshComplianceQ.data.summary.in_progress
    : 0;
  // (c) Overdue statutory obligations — HR-admin gated compliance
  //     dashboard (items arrive due-date-sorted server-side).
  const statutoryQ = useQuery({
    queryKey: ["compliance", "dashboard"],
    queryFn: ({ signal }) => complianceApi.getComplianceDashboard(signal),
    enabled: isHRAdmin && platform,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const overdueItems = (statutoryQ.data?.items ?? []).filter(
    (i) => i.status === "overdue",
  );
  const nextOverdue = overdueItems[0];

  const attentionLoading =
    approvalsQ.isLoading ||
    (isHRAdmin && hasPosh && poshComplianceQ.isLoading) ||
    (isHRAdmin && platform && statutoryQ.isLoading);
  const hasAttentionRows =
    pendingApprovals > 0 || poshNonCompliant > 0 || overdueItems.length > 0;

  // The same Manage items as the sidebar, so every tile is a page the
  // viewer can use, named as in the sidebar.
  const { has } = useFeatures();
  const navFlags = useNavFlags();
  const visibleModules = useMemo(
    () => overviewModules(user?.role, has, navFlags),
    [user?.role, has, navFlags],
  );

  // Dormant modules (docs/56 U1): entitled by the plan but switched OFF in
  // Settings → Modules. HR-admins see them as "Activate" tiles so a module
  // that was turned off stays one click from coming back — never a mystery
  // absence. The GET is hrRole-gated, so only fire it for HR admins.
  const modulesQ = useQuery({
    queryKey: ["settings", "modules"],
    queryFn: ({ signal }) => settingsApi.getModules(signal),
    enabled: isHRAdmin,
    retry: false,
  });
  const dormant = useMemo(
    () => (modulesQ.data?.modules ?? []).filter((m) => !m.enabled),
    [modulesQ.data],
  );

  return (
    <div className="space-y-6">
      {/* Company admins and HR land here after sign-in (persona round
          2026-10), so the trial countdown / mandatory-2FA prompt that the
          Me home shows must be here too. Dismissal is shared (same keys). */}
      <FirstRunBanners />
      <PageHeader
        title="Overview"
        description={
          user?.tenant
            ? `What needs your attention at ${user.tenant.name}.`
            : `Welcome back, ${name}.`
        }
      />

      {/* Needs your attention — actionable counts, above everything else.
          Rows are fail-soft (hidden while loading / on error); when every
          feed is quiet the operator still gets explicit reassurance. */}
      {(hasAttentionRows || !attentionLoading) && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Needs Your Attention</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {hasAttentionRows ? (
            <ul className="divide-y divide-border">
              {pendingApprovals > 0 && (
                <li>
                  <Link
                    to="/app/$tenantSlug/approvals"
                    params={{ tenantSlug: user?.tenant?.slug ?? "" }}
                    className="group flex items-center gap-3 py-3 transition-colors hover:bg-muted/40 sm:-mx-3 sm:rounded-md sm:px-3"
                  >
                    <Inbox className="h-5 w-5 shrink-0 text-primary" />
                    <span className="flex-1 text-sm text-foreground group-hover:text-primary">
                      <span className="font-medium">{pendingApprovals}</span>{" "}
                      {pendingApprovals === 1 ? "approval" : "approvals"}{" "}
                      waiting for you
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              )}
              {poshNonCompliant > 0 && (
                <li>
                  <Link
                    to="/app/$tenantSlug/posh/compliance"
                    params={{ tenantSlug: user?.tenant?.slug ?? "" }}
                    className="group flex items-center gap-3 py-3 transition-colors hover:bg-muted/40 sm:-mx-3 sm:rounded-md sm:px-3"
                  >
                    <ShieldCheck className="h-5 w-5 shrink-0 text-amber-600" />
                    <span className="flex-1 text-sm text-foreground group-hover:text-primary">
                      <span className="font-medium">{poshNonCompliant}</span>{" "}
                      {poshNonCompliant === 1 ? "employee" : "employees"} not
                      compliant with POSH training
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              )}
              {overdueItems.length > 0 && (
                <li>
                  <Link
                    to="/app/$tenantSlug/compliance"
                    params={{ tenantSlug: user?.tenant?.slug ?? "" }}
                    className="group flex items-center gap-3 py-3 transition-colors hover:bg-muted/40 sm:-mx-3 sm:rounded-md sm:px-3"
                  >
                    <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
                    <span className="flex-1 text-sm text-foreground group-hover:text-primary">
                      <span className="font-medium">
                        {overdueItems.length}
                      </span>{" "}
                      statutory{" "}
                      {overdueItems.length === 1
                        ? "obligation"
                        : "obligations"}{" "}
                      overdue
                      {nextOverdue ? ` — next: ${nextOverdue.title}` : ""}
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              )}
            </ul>
          ) : (
            <p className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              Nothing needs your attention right now.
            </p>
          )}
        </CardContent>
      </Card>
      )}

      <AccountingHero />

      {/* First-run checklist — real done-signals, server-derived. */}
      <SetupChecklist variant="full" />

      {visibleModules.length > 0 && (
      <div>
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">
          Jump into a Module
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleModules.map((m) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.href}
                to={resolveTenantPath(m.href, user?.tenant?.slug ?? "")}
                search={m.search as never}
                className="group rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span title={m.label} className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                        {m.label}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {m.blurb}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      )}

      {/* Dormant modules — entitled but switched off (docs/56 U1). */}
      {isHRAdmin && dormant.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">
            Switched Off
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dormant.map((m) => {
              const meta = MODULE_META[m.feature] ?? {
                label: m.feature,
                blurb: "",
              };
              return (
                <Link
                  key={m.feature}
                  to="/app/$tenantSlug/settings/modules"
                  params={{ tenantSlug: user?.tenant?.slug ?? "" }}
                  className="group rounded-lg border border-dashed border-border bg-card/50 p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-muted-foreground group-hover:text-primary">
                        {meta.label}
                      </span>
                      <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground/70">
                        {meta.blurb}
                      </div>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-primary">
                      Activate
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {isHRAdmin && poshOnly && <NotInPoshOnlyTiles />}
    </div>
  );
}
